import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Users, Calendar, ClipboardList, Award, ArrowLeft, Download, UserCheck, FileText, TrendingUp, Activity, Sparkles, Brain, Clock, XCircle, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CandidateList } from "@/components/hr/CandidateList";
import { InterviewScheduler } from "@/components/hr/InterviewScheduler";
import { TestManagement } from "@/components/hr/TestManagement";
import { WorkflowOverview } from "@/components/hr/WorkflowOverview";
import { AddCandidateDialog } from "@/components/hr/AddCandidateDialog";
import { Demographics } from "@/components/hr/Demographics";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import AppLayout from "@/components/layout/AppLayout";
import HiringAnalytics from "./components/HiringAnalytics";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useLanguage } from "@/contexts/LanguageContext";
interface HRMetrics {
  activeCandidates: number;
  pendingInterviews: number;
  pendingTests: number;
  readyToHire: number;
}
export default function HRDashboard() {
  const navigate = useNavigate();
  const {
    t
  } = useLanguage();
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [candidateListKey, setCandidateListKey] = useState(0);
  const [metrics, setMetrics] = useState<HRMetrics>({
    activeCandidates: 0,
    pendingInterviews: 0,
    pendingTests: 0,
    readyToHire: 0
  });
  const {
    isSuperAdmin,
    isAdmin,
    isUnassignedAdmin,
    adminTeamIds
  } = useUserRoles();
  const handleCandidateAdded = () => {
    fetchMetrics();
    setCandidateListKey(prev => prev + 1); // Force re-render of CandidateList
  };
  useEffect(() => {
    fetchMetrics();
  }, []);
  const fetchMetrics = async () => {
    try {
      // Unassigned admins see no metrics
      if (isUnassignedAdmin) {
        setMetrics({
          activeCandidates: 0,
          pendingInterviews: 0,
          pendingTests: 0,
          readyToHire: 0
        });
        return;
      }

      // Build candidates query based on permissions
      let candidatesQuery = supabase.from('candidates').select('*', {
        count: 'exact',
        head: true
      }).neq('status', 'hired').neq('status', 'rejected').neq('status', 'archived');

      // Team admins see candidates they created OR from managers in their teams
      if (isAdmin && !isSuperAdmin && adminTeamIds.length > 0) {
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        if (user) {
          // Get all managers in the admin's teams
          const {
            data: teamManagers
          } = await supabase.from('manager_teams').select('manager_id').in('team_id', adminTeamIds);
          const managerIds = teamManagers?.map(m => m.manager_id) || [];
          const allowedCreatorIds = [user.id, ...managerIds];
          candidatesQuery = candidatesQuery.in('created_by', allowedCreatorIds);
        }
      }
      const {
        count: activeCandidates
      } = await candidatesQuery;

      // Fetch pending interviews (scheduled but not completed)
      const {
        count: pendingInterviews
      } = await supabase.from('interviews').select('*', {
        count: 'exact',
        head: true
      }).eq('status', 'scheduled');

      // Fetch pending tests (not completed)
      const {
        count: pendingTests
      } = await supabase.from('test_results').select('*', {
        count: 'exact',
        head: true
      }).neq('status', 'completed');

      // Fetch ready to hire candidates (those in final_decision stage and not hired)
      const {
        data: finalStageRows
      } = await supabase.from('hr_workflow_stages').select('candidate_id, status').eq('stage_name', 'final_decision').in('status', ['available', 'in_progress']);
      let readyToHire = 0;
      if (finalStageRows && finalStageRows.length > 0) {
        const ids = finalStageRows.map(r => r.candidate_id);
        const {
          data: candidateRows
        } = await supabase.from('candidates').select('id, status').in('id', ids);
        readyToHire = (candidateRows || []).filter(c => c.status !== 'hired' && c.status !== 'rejected').length;
      }
      setMetrics({
        activeCandidates: activeCandidates || 0,
        pendingInterviews: pendingInterviews || 0,
        pendingTests: pendingTests || 0,
        readyToHire: readyToHire || 0
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };
  const generateHiringProcessDoc = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hiring Process Documentation</title>
        <style>
          body { font-family: 'Arial', sans-serif; margin: 40px; line-height: 1.6; color: #333; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
          h1 { color: #1e40af; margin-bottom: 10px; }
          h2 { color: #2563eb; border-left: 4px solid #3b82f6; padding-left: 15px; margin-top: 30px; }
          h3 { color: #1d4ed8; }
          .process-step { background: #f8fafc; border: 1px solid #e2e8f0; padding: 20px; margin: 15px 0; border-radius: 8px; }
          .step-number { background: #3b82f6; color: white; border-radius: 50%; width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 15px; }
          .metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 30px 0; }
          .metric-card { background: linear-gradient(135deg, #3b82f6, #1d4ed8); color: white; padding: 20px; border-radius: 10px; text-align: center; }
          .metric-number { font-size: 2.5em; font-weight: bold; }
          .metric-label { font-size: 0.9em; opacity: 0.9; }
          .footer { margin-top: 50px; text-align: center; font-size: 0.9em; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 20px; }
          ul { padding-left: 20px; }
          li { margin-bottom: 8px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">OVIT GUARDLY</div>
          <h1>Hiring Process Documentation</h1>
          <p>Comprehensive Recruitment and Onboarding Procedures</p>
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
        </div>

        <div class="metrics">
          <div class="metric-card">
            <div class="metric-number">${metrics.activeCandidates}</div>
            <div class="metric-label">Active Candidates</div>
          </div>
          <div class="metric-card">
            <div class="metric-number">${metrics.pendingInterviews}</div>
            <div class="metric-label">Pending Interviews</div>
          </div>
          <div class="metric-card">
            <div class="metric-number">${metrics.pendingTests}</div>
            <div class="metric-label">Pending Tests</div>
          </div>
          <div class="metric-card">
            <div class="metric-number">${metrics.readyToHire}</div>
            <div class="metric-label">Ready to Hire</div>
          </div>
        </div>

        <h2>Process Overview</h2>
        <p>Our hiring process is designed to ensure we recruit the most qualified and suitable candidates for security positions. The process consists of six main stages, each with specific evaluation criteria and documentation requirements.</p>

        <h2>Hiring Process Stages</h2>

        <div class="process-step">
          <h3><span class="step-number">1</span>Application Submission & Initial Screening</h3>
          <p><strong>Duration:</strong> 1-2 business days</p>
          <p><strong>Objective:</strong> Collect and evaluate initial candidate information</p>
          <ul>
            <li>Online application form completion</li>
            <li>Resume and cover letter review</li>
            <li>Basic qualification verification</li>
            <li>Criminal background check initiation</li>
            <li>Initial screening call (15-20 minutes)</li>
          </ul>
          <p><strong>Decision Criteria:</strong> Minimum qualifications met, clear communication, no disqualifying factors</p>
        </div>

        <div class="process-step">
          <h3><span class="step-number">2</span>First Interview</h3>
          <p><strong>Duration:</strong> 45-60 minutes</p>
          <p><strong>Objective:</strong> Assess candidate's experience, motivation, and cultural fit</p>
          <ul>
            <li>Structured behavioral interview</li>
            <li>Work history review</li>
            <li>Scenario-based questions</li>
            <li>Company culture and values discussion</li>
            <li>Salary and benefits overview</li>
          </ul>
          <p><strong>Decision Criteria:</strong> Professional demeanor, relevant experience, cultural alignment</p>
        </div>

        <div class="process-step">
          <h3><span class="step-number">3</span>Second Interview</h3>
          <p><strong>Duration:</strong> 60-90 minutes</p>
          <p><strong>Objective:</strong> Deep-dive technical assessment and management evaluation</p>
          <ul>
            <li>Technical skills assessment</li>
            <li>Security protocol knowledge test</li>
            <li>Emergency response scenarios</li>
            <li>Management team interview</li>
            <li>Reference check completion</li>
          </ul>
          <p><strong>Decision Criteria:</strong> Technical competency, problem-solving ability, leadership potential</p>
        </div>

        <div class="process-step">
          <h3><span class="step-number">4</span>Cognitive Assessment</h3>
          <p><strong>Duration:</strong> 45 minutes</p>
          <p><strong>Objective:</strong> Evaluate cognitive abilities and problem-solving skills</p>
          <ul>
            <li>Logic and reasoning test</li>
            <li>Attention to detail assessment</li>
            <li>Critical thinking evaluation</li>
            <li>Pattern recognition test</li>
            <li>Decision-making scenarios</li>
          </ul>
          <p><strong>Decision Criteria:</strong> Minimum score threshold, cognitive stability indicators</p>
        </div>

        <div class="process-step">
          <h3><span class="step-number">5</span>Psychological Assessment (IPIP-NEO)</h3>
          <p><strong>Duration:</strong> 90 minutes</p>
          <p><strong>Objective:</strong> Comprehensive personality and psychological evaluation</p>
          <ul>
            <li>240-question IPIP-NEO personality inventory</li>
            <li>Big Five personality traits assessment</li>
            <li>Stress tolerance evaluation</li>
            <li>Emotional stability indicators</li>
            <li>Professional psychology review</li>
          </ul>
          <p><strong>Decision Criteria:</strong> Psychological fitness for security work, stress management capability</p>
        </div>

        <div class="process-step">
          <h3><span class="step-number">6</span>Final Decision & Onboarding</h3>
          <p><strong>Duration:</strong> 2-3 business days</p>
          <p><strong>Objective:</strong> Make final hiring decision and begin onboarding process</p>
          <ul>
            <li>Comprehensive candidate review</li>
            <li>Background check completion</li>
            <li>Medical clearance (if required)</li>
            <li>Job offer preparation and negotiation</li>
            <li>Onboarding schedule preparation</li>
          </ul>
          <p><strong>Decision Criteria:</strong> Overall assessment score, team consensus, business need alignment</p>
        </div>

        <h2>Quality Assurance</h2>
        <ul>
          <li>All interviews are conducted by certified HR personnel</li>
          <li>Standardized evaluation forms ensure consistent assessment</li>
          <li>Multiple evaluators involved in final decisions</li>
          <li>Regular process review and improvement</li>
          <li>Compliance with employment law and regulations</li>
        </ul>

        <h2>Documentation Requirements</h2>
        <ul>
          <li>Completed application forms and supporting documents</li>
          <li>Interview evaluation sheets from all stages</li>
          <li>Test results and psychological assessment reports</li>
          <li>Background check and reference verification records</li>
          <li>Final decision rationale and approval documentation</li>
        </ul>

        <div class="footer">
          <p>This document is confidential and proprietary to OVIT GUARDLY</p>
          <p>Document prepared for audit and compliance purposes</p>
        </div>
      </body>
      </html>
    `;
  };
  const generateIncidentHandlingDoc = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Incident Handling Documentation</title>
        <style>
          body { font-family: 'Arial', sans-serif; margin: 40px; line-height: 1.6; color: #333; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #dc2626; margin-bottom: 10px; }
          h1 { color: #b91c1c; margin-bottom: 10px; }
          h2 { color: #dc2626; border-left: 4px solid #ef4444; padding-left: 15px; margin-top: 30px; }
          h3 { color: #b91c1c; }
          .severity-high { background: #fef2f2; border: 2px solid #fca5a5; color: #991b1b; }
          .severity-medium { background: #fefce8; border: 2px solid #fde047; color: #a16207; }
          .severity-low { background: #f0fdf4; border: 2px solid #86efac; color: #166534; }
          .incident-type { padding: 20px; margin: 15px 0; border-radius: 8px; }
          .process-flow { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin: 30px 0; }
          .flow-step { background: #f8fafc; border: 1px solid #e2e8f0; padding: 20px; border-radius: 8px; text-align: center; }
          .arrow { text-align: center; font-size: 2em; color: #dc2626; margin: 10px 0; }
          .footer { margin-top: 50px; text-align: center; font-size: 0.9em; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 20px; }
          .emergency-contact { background: #fee2e2; border: 2px solid #fca5a5; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .contact-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
          .contact-card { background: white; padding: 15px; border-radius: 6px; text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">OVIT GUARDLY</div>
          <h1>Incident Handling Procedures</h1>
          <p>Emergency Response and Incident Management Protocol</p>
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
        </div>

        <h2>Emergency Contacts</h2>
        <div class="emergency-contact">
          <h3>Critical Emergency Numbers</h3>
          <div class="contact-grid">
            <div class="contact-card">
              <strong>Police Emergency</strong><br>
              100
            </div>
            <div class="contact-card">
              <strong>Fire Department</strong><br>
              199
            </div>
            <div class="contact-card">
              <strong>Medical Emergency</strong><br>
              166
            </div>
            <div class="contact-card">
              <strong>Crisis Management Team</strong><br>
              +30 210 XXX XXXX
            </div>
          </div>
        </div>

        <h2>Incident Classification</h2>

        <div class="incident-type severity-high">
          <h3>HIGH SEVERITY INCIDENTS</h3>
          <p><strong>Response Time:</strong> Immediate (0-5 minutes)</p>
          <ul>
            <li>Security breaches with immediate threat to life or property</li>
            <li>Armed intrusions or attacks</li>
            <li>Fire or explosion incidents</li>
            <li>Medical emergencies requiring immediate intervention</li>
            <li>Natural disasters or extreme weather events</li>
          </ul>
        </div>

        <div class="incident-type severity-medium">
          <h3>MEDIUM SEVERITY INCIDENTS</h3>
          <p><strong>Response Time:</strong> 5-15 minutes</p>
          <ul>
            <li>Unauthorized access attempts</li>
            <li>Suspicious activity or persons</li>
            <li>Equipment malfunction affecting security</li>
            <li>Minor medical incidents</li>
            <li>Vandalism or property damage</li>
          </ul>
        </div>

        <div class="incident-type severity-low">
          <h3>LOW SEVERITY INCIDENTS</h3>
          <p><strong>Response Time:</strong> 15-30 minutes</p>
          <ul>
            <li>Routine security checks and documentation</li>
            <li>Non-critical equipment issues</li>
            <li>Minor policy violations</li>
            <li>Administrative incidents</li>
            <li>Customer service issues</li>
          </ul>
        </div>

        <h2>Incident Response Process Flow</h2>
        <div class="process-flow">
          <div class="flow-step">
            <h4>1. DETECTION</h4>
            <p>Immediate identification of incident through monitoring systems, reports, or observation</p>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step">
            <h4>2. ASSESSMENT</h4>
            <p>Quick evaluation of severity, scope, and immediate risks to determine response level</p>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step">
            <h4>3. RESPONSE</h4>
            <p>Immediate action taken according to incident severity and established protocols</p>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step">
            <h4>4. CONTAINMENT</h4>
            <p>Measures to prevent escalation and limit impact on personnel and property</p>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step">
            <h4>5. INVESTIGATION</h4>
            <p>Detailed analysis to determine cause, collect evidence, and document findings</p>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step">
            <h4>6. RESOLUTION</h4>
            <p>Implementation of corrective actions and restoration of normal operations</p>
          </div>
        </div>

        <h2>Reporting Requirements</h2>
        <ul>
          <li><strong>Immediate Report:</strong> Verbal notification within 5 minutes for high severity incidents</li>
          <li><strong>Initial Written Report:</strong> Basic incident details within 30 minutes</li>
          <li><strong>Detailed Report:</strong> Comprehensive documentation within 24 hours</li>
          <li><strong>Follow-up Reports:</strong> Status updates until resolution</li>
          <li><strong>Final Report:</strong> Complete analysis and lessons learned within 72 hours</li>
        </ul>

        <h2>Documentation Standards</h2>
        <ul>
          <li>Incident ID and timestamp</li>
          <li>Location and personnel involved</li>
          <li>Detailed description of events</li>
          <li>Actions taken and by whom</li>
          <li>Evidence collected (photos, witness statements)</li>
          <li>Impact assessment and damages</li>
          <li>Corrective measures implemented</li>
          <li>Recommendations for prevention</li>
        </ul>

        <h2>Quality Assurance</h2>
        <ul>
          <li>Regular training and drill exercises</li>
          <li>Monthly review of incident reports</li>
          <li>Continuous improvement of procedures</li>
          <li>Compliance with industry standards</li>
          <li>Performance metrics tracking</li>
        </ul>

        <div class="footer">
          <p>This document is confidential and proprietary to OVIT GUARDLY</p>
          <p>Document prepared for audit and compliance purposes</p>
        </div>
      </body>
      </html>
    `;
  };
  const generateLeaveManagementDoc = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Leave Management Documentation</title>
        <style>
          body { font-family: 'Arial', sans-serif; margin: 40px; line-height: 1.6; color: #333; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 3px solid #059669; padding-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #059669; margin-bottom: 10px; }
          h1 { color: #047857; margin-bottom: 10px; }
          h2 { color: #059669; border-left: 4px solid #10b981; padding-left: 15px; margin-top: 30px; }
          h3 { color: #047857; }
          .leave-type { background: #f0fdf4; border: 1px solid #bbf7d0; padding: 20px; margin: 15px 0; border-radius: 8px; }
          .workflow-step { background: #f8fafc; border: 1px solid #e2e8f0; padding: 20px; margin: 15px 0; border-radius: 8px; }
          .step-number { background: #10b981; color: white; border-radius: 50%; width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 15px; }
          .policy-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; margin: 30px 0; }
          .policy-card { background: linear-gradient(135deg, #10b981, #047857); color: white; padding: 20px; border-radius: 10px; }
          .footer { margin-top: 50px; text-align: center; font-size: 0.9em; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 20px; }
          .approval-matrix { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .approval-matrix th, .approval-matrix td { border: 1px solid #d1d5db; padding: 12px; text-align: left; }
          .approval-matrix th { background: #f3f4f6; font-weight: bold; }
          .balance-calculation { background: #ecfdf5; border: 1px solid #bbf7d0; padding: 20px; border-radius: 8px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">OVIT GUARDLY</div>
          <h1>Leave Management System</h1>
          <p>Comprehensive Leave Policy and Management Procedures</p>
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
        </div>

        <h2>Leave Types and Entitlements</h2>

        <div class="leave-type">
          <h3>Annual Leave</h3>
          <ul>
            <li><strong>Entitlement:</strong> 20 working days per calendar year</li>
            <li><strong>Accrual:</strong> 1.67 days per month</li>
            <li><strong>Carry Over:</strong> Maximum 5 days to following year</li>
            <li><strong>Notice Required:</strong> 14 days minimum</li>
            <li><strong>Approval Level:</strong> Direct supervisor</li>
          </ul>
        </div>

        <div class="leave-type">
          <h3>Sick Leave</h3>
          <ul>
            <li><strong>Entitlement:</strong> 10 days per calendar year</li>
            <li><strong>Medical Certificate:</strong> Required for 3+ consecutive days</li>
            <li><strong>Notice Required:</strong> As soon as possible</li>
            <li><strong>Approval Level:</strong> HR Department</li>
            <li><strong>Carry Over:</strong> Unused days accumulate up to 30 days maximum</li>
          </ul>
        </div>

        <div class="leave-type">
          <h3>Emergency Leave</h3>
          <ul>
            <li><strong>Entitlement:</strong> 3 days per calendar year</li>
            <li><strong>Purpose:</strong> Family emergencies, urgent personal matters</li>
            <li><strong>Notice Required:</strong> Immediate notification required</li>
            <li><strong>Documentation:</strong> Supporting evidence may be requested</li>
            <li><strong>Approval Level:</strong> HR Manager</li>
          </ul>
        </div>

        <div class="leave-type">
          <h3>Maternity/Paternity Leave</h3>
          <ul>
            <li><strong>Maternity Leave:</strong> 17 weeks as per Greek labor law</li>
            <li><strong>Paternity Leave:</strong> 14 days as per Greek labor law</li>
            <li><strong>Notice Required:</strong> 2 months before expected date</li>
            <li><strong>Documentation:</strong> Medical certificate required</li>
            <li><strong>Approval Level:</strong> HR Director</li>
          </ul>
        </div>

        <h2>Leave Request Workflow</h2>

        <div class="workflow-step">
          <h3><span class="step-number">1</span>Employee Submission</h3>
          <ul>
            <li>Access leave management system</li>
            <li>Complete leave request form</li>
            <li>Select leave type and dates</li>
            <li>Provide justification/reason</li>
            <li>Attach supporting documents if required</li>
            <li>Submit for approval</li>
          </ul>
        </div>

        <div class="workflow-step">
          <h3><span class="step-number">2</span>Supervisor Review</h3>
          <ul>
            <li>Receive automated notification</li>
            <li>Review leave request details</li>
            <li>Check work schedule and coverage</li>
            <li>Verify leave balance availability</li>
            <li>Approve, reject, or request modifications</li>
            <li>Provide feedback if rejected</li>
          </ul>
        </div>

        <div class="workflow-step">
          <h3><span class="step-number">3</span>HR Verification</h3>
          <ul>
            <li>Verify leave balance and entitlements</li>
            <li>Check policy compliance</li>
            <li>Review supporting documentation</li>
            <li>Update leave records</li>
            <li>Generate approval confirmation</li>
            <li>Schedule calendar updates</li>
          </ul>
        </div>

        <div class="workflow-step">
          <h3><span class="step-number">4</span>Final Approval & Processing</h3>
          <ul>
            <li>Final approval from designated authority</li>
            <li>Payroll system updates</li>
            <li>Schedule adjustments</li>
            <li>Coverage arrangements</li>
            <li>Employee notification</li>
            <li>Record archival</li>
          </ul>
        </div>

        <h2>Approval Matrix</h2>
        <table class="approval-matrix">
          <thead>
            <tr>
              <th>Leave Type</th>
              <th>Duration</th>
              <th>First Approver</th>
              <th>Final Approver</th>
              <th>Notice Period</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Annual Leave</td>
              <td>1-5 days</td>
              <td>Direct Supervisor</td>
              <td>HR Coordinator</td>
              <td>14 days</td>
            </tr>
            <tr>
              <td>Annual Leave</td>
              <td>6+ days</td>
              <td>Direct Supervisor</td>
              <td>HR Manager</td>
              <td>30 days</td>
            </tr>
            <tr>
              <td>Sick Leave</td>
              <td>Any duration</td>
              <td>HR Coordinator</td>
              <td>HR Manager</td>
              <td>ASAP</td>
            </tr>
            <tr>
              <td>Emergency Leave</td>
              <td>Any duration</td>
              <td>HR Manager</td>
              <td>HR Director</td>
              <td>Immediate</td>
            </tr>
            <tr>
              <td>Maternity/Paternity</td>
              <td>Statutory period</td>
              <td>HR Manager</td>
              <td>HR Director</td>
              <td>60 days</td>
            </tr>
          </tbody>
        </table>

        <h2>Leave Balance Calculation</h2>
        <div class="balance-calculation">
          <h3>Annual Leave Accrual Formula</h3>
          <p><strong>Monthly Accrual = Annual Entitlement ÷ 12</strong></p>
          <p><strong>Daily Accrual = Annual Entitlement ÷ 365</strong></p>
          <p><strong>Pro-rata Calculation:</strong> For partial year employment, entitlement is calculated from start date</p>
          
          <h4>Example Calculation:</h4>
          <ul>
            <li>Employee starts on March 1st</li>
            <li>Remaining months: 10 months</li>
            <li>Pro-rata entitlement: (20 days ÷ 12) × 10 = 16.67 days</li>
          </ul>
        </div>

        <h2>System Features</h2>
        <div class="policy-grid">
          <div class="policy-card">
            <h4>Automated Workflows</h4>
            <ul>
              <li>Digital leave request submission</li>
              <li>Multi-level approval routing</li>
              <li>Automated notifications</li>
              <li>Calendar integration</li>
            </ul>
          </div>
          <div class="policy-card">
            <h4>Balance Tracking</h4>
            <ul>
              <li>Real-time balance updates</li>
              <li>Accrual calculations</li>
              <li>Carry-over management</li>
              <li>Reporting dashboards</li>
            </ul>
          </div>
          <div class="policy-card">
            <h4>Compliance Features</h4>
            <ul>
              <li>Legal requirement tracking</li>
              <li>Policy enforcement rules</li>
              <li>Audit trail maintenance</li>
              <li>Reporting capabilities</li>
            </ul>
          </div>
        </div>

        <h2>Reporting and Analytics</h2>
        <ul>
          <li><strong>Leave Utilization Reports:</strong> Monthly and annual usage statistics</li>
          <li><strong>Balance Reports:</strong> Individual and department leave balances</li>
          <li><strong>Trend Analysis:</strong> Leave patterns and seasonal variations</li>
          <li><strong>Compliance Reports:</strong> Adherence to policies and legal requirements</li>
          <li><strong>Forecast Reports:</strong> Projected leave impact on operations</li>
        </ul>

        <h2>Quality Assurance</h2>
        <ul>
          <li>Regular policy review and updates</li>
          <li>System accuracy verification</li>
          <li>Employee feedback collection</li>
          <li>Legal compliance monitoring</li>
          <li>Continuous process improvement</li>
        </ul>

        <div class="footer">
          <p>This document is confidential and proprietary to OVIT GUARDLY</p>
          <p>Document prepared for audit and compliance purposes</p>
        </div>
      </body>
      </html>
    `;
  };
  const downloadDocument = (htmlContent: string, filename: string) => {
    const blob = new Blob([htmlContent], {
      type: 'text/html'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 relative overflow-hidden">
        {/* Enhanced background patterns */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-blue-500/10 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-radial from-primary/10 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" style={{
        animationDelay: '2s'
      }}></div>
        
        <div className="relative z-10 space-y-8 animate-fadeIn px-4 sm:px-6 lg:px-8 pt-6 pb-12">
          {/* Enhanced Header Section */}
          <div className="space-y-6">
            <Button variant="outline" size="sm" className="flex items-center gap-2 hover-scale bg-background/80 backdrop-blur-sm border-primary/20 hover:border-primary/40 transition-all duration-300" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4" />
              {t('backToDashboard')}
            </Button>
            
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-luxury animate-pulse">
                  <UserCheck className="h-8 w-8 text-white" />
                </div>
                <div className="h-8 w-px bg-gradient-to-b from-transparent via-primary/50 to-transparent"></div>
                <div className="p-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-luxury animate-pulse" style={{
                animationDelay: '0.5s'
              }}>
                  <Brain className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-blue-500 to-primary bg-clip-text text-transparent leading-tight">
                {t('humanResourcesManagement')}
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto font-medium">
                {t('comprehensiveRecruitment')}
              </p>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap items-center justify-center gap-6 pt-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-blue-500/20 shadow-sm">
                  <TrendingUp className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">{t('activeRecruitment')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-primary/20 shadow-sm">
                  <Activity className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{t('smartAssessment')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-blue-500/10 backdrop-blur-sm rounded-full border border-primary/30 shadow-sm">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">{t('realTimeTracking')}</span>
                </div>
              </div>
            </div>
            
            {/* Enhanced Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              
              
              <Button onClick={() => setShowAddCandidate(true)} className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale">
                <Plus className="mr-2 h-4 w-4" />
                {t('addCandidate')}
              </Button>
              
              <Button variant="outline" onClick={() => navigate("/hr/demographics")} className="w-full sm:w-auto bg-background/80 backdrop-blur-sm border-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300 hover-scale text-emerald-600 hover:text-emerald-700">
                <BarChart3 className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">{t('demographics')}</span>
                <span className="sm:hidden">{t('demographics')}</span>
              </Button>
            </div>
          </div>

          {/* Enhanced Overview Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className={cn("group relative overflow-hidden cursor-default transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury", "bg-gradient-to-br from-background/95 via-background/90 to-blue-50/50 border-blue-200/50", "hover:from-background/90 hover:to-background/95 hover:border-blue-400/70", "backdrop-blur-sm")}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 group-hover:from-blue-500/15 group-hover:to-indigo-500/15 transition-all duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              
              <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground group-hover:text-blue-700 transition-colors">{t('activeCandidates')}</CardTitle>
                <div className="p-2 bg-gradient-to-br from-blue-100/80 to-blue-50/60 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-2xl font-bold text-foreground group-hover:text-blue-700 transition-colors">
                  {metrics.activeCandidates}
                </div>
                <p className="text-xs text-muted-foreground group-hover:text-blue-600 transition-colors">{t('totalInProcess')}</p>
              </CardContent>
            </Card>

            <Card className={cn("group relative overflow-hidden cursor-default transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury", "bg-gradient-to-br from-background/95 via-background/90 to-purple-50/50 border-purple-200/50", "hover:from-background/90 hover:to-background/95 hover:border-purple-400/70", "backdrop-blur-sm")}>
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 group-hover:from-purple-500/15 group-hover:to-indigo-500/15 transition-all duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              
              <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground group-hover:text-purple-700 transition-colors">{t('pendingInterviews')}</CardTitle>
                <div className="p-2 bg-gradient-to-br from-purple-100/80 to-purple-50/60 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="h-4 w-4 text-purple-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-2xl font-bold text-foreground group-hover:text-purple-700 transition-colors">
                  {metrics.pendingInterviews}
                </div>
                <p className="text-xs text-muted-foreground group-hover:text-purple-600 transition-colors">{t('scheduled')}</p>
              </CardContent>
            </Card>

            <Card className={cn("group relative overflow-hidden cursor-default transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury", "bg-gradient-to-br from-background/95 via-background/90 to-amber-50/50 border-amber-200/50", "hover:from-background/90 hover:to-background/95 hover:border-amber-400/70", "backdrop-blur-sm")}>
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 group-hover:from-amber-500/15 group-hover:to-orange-500/15 transition-all duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              
              <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground group-hover:text-amber-700 transition-colors">{t('pendingTests')}</CardTitle>
                <div className="p-2 bg-gradient-to-br from-amber-100/80 to-amber-50/60 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <ClipboardList className="h-4 w-4 text-amber-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-2xl font-bold text-foreground group-hover:text-amber-700 transition-colors">
                  {metrics.pendingTests}
                </div>
                <p className="text-xs text-muted-foreground group-hover:text-amber-700 transition-colors">{t('awaitingResults')}</p>
              </CardContent>
            </Card>

            <Card className={cn("group relative overflow-hidden cursor-default transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury", "bg-gradient-to-br from-background/95 via-background/90 to-emerald-50/50 border-emerald-200/50", "hover:from-background/90 hover:to-background/95 hover:border-emerald-400/70", "backdrop-blur-sm")}>
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-500/5 group-hover:from-emerald-500/15 group-hover:to-green-500/15 transition-all duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              
              <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground group-hover:text-emerald-700 transition-colors">{t('readyToHire')}</CardTitle>
                <div className="p-2 bg-gradient-to-br from-emerald-100/80 to-emerald-50/60 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <Award className="h-4 w-4 text-emerald-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-2xl font-bold text-foreground group-hover:text-emerald-700 transition-colors">
                  {metrics.readyToHire}
                </div>
                <p className="text-xs text-muted-foreground group-hover:text-emerald-700 transition-colors">{t('completedAllStages')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Hiring Analytics */}
          <HiringAnalytics />

          {/* Enhanced Tabs Section */}
          <Tabs defaultValue="candidates" className="space-y-6">
            <div className="flex justify-center">
              <TabsList className="grid grid-cols-6 bg-gradient-to-r from-muted/80 to-muted/60 backdrop-blur-sm border border-primary/20 shadow-lg rounded-xl p-2 h-14">
                <TabsTrigger value="candidates" className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('candidates')}</span>
                </TabsTrigger>
                <TabsTrigger value="tests" className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg">
                  <ClipboardList className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tests')}</span>
                </TabsTrigger>
                <TabsTrigger value="workflow" className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg">
                  <Activity className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('workflowTab')}</span>
                </TabsTrigger>
                <TabsTrigger value="hired" className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg">
                  <UserCheck className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('hired')}</span>
                </TabsTrigger>
                <TabsTrigger value="rejected" className="flex items-center justify-center gap-2 h-10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-300 hover-scale rounded-lg">
                  <XCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('rejected')}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="candidates" className="animate-fadeIn">
              <CandidateList key={candidateListKey} onCandidateChange={fetchMetrics} statusFilter="under_review" />
            </TabsContent>

            <TabsContent value="tests" className="animate-fadeIn">
              <TestManagement />
            </TabsContent>

            <TabsContent value="workflow" className="animate-fadeIn">
              <WorkflowOverview />
            </TabsContent>

            <TabsContent value="hired" className="animate-fadeIn">
              <CandidateList onCandidateChange={fetchMetrics} statusFilter="hired" />
            </TabsContent>

            <TabsContent value="rejected" className="animate-fadeIn">
              <CandidateList onCandidateChange={fetchMetrics} statusFilter="rejected" />
            </TabsContent>
          </Tabs>

          {/* Enhanced Footer */}
          <footer className="text-center py-8 mt-16">
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-blue-500" />
                <span>{t('hrManagementSystem')}</span>
              </div>
              <div className="h-4 w-px bg-muted-foreground/30"></div>
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-green-500 animate-pulse" />
                <span>{t('systemActive')}</span>
              </div>
            </div>
          </footer>
        </div>

        <AddCandidateDialog open={showAddCandidate} onOpenChange={setShowAddCandidate} onCandidateAdded={handleCandidateAdded} />
      </div>
    </AppLayout>;
}